// src/hooks/useConversation.js
// ═══════════════════════════════════════════════════════════════════════════════
// CONVERSATION MANAGER - DO NOT MODIFY THIS FILE
// This hook manages conversation history for AI chat
// It solves React's stale closure problem by using useRef for synchronous access
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const STORAGE_KEY = 'airgati_conversation_history';
const MAX_HISTORY_LENGTH = 24; // Keep last 12 turns (24 messages: user + assistant)
const SAVE_DEBOUNCE_MS = 1000;

// ═══════════════════════════════════════════════════════════════════════════════
// THE HOOK
// ═══════════════════════════════════════════════════════════════════════════════

export function useConversation(userId = null) {
  // ─────────────────────────────────────────────────────────────────────────────
  // State for React re-renders (UI updates)
  // ─────────────────────────────────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // Ref for synchronous access (SOLVES STALE CLOSURE BUG)
  // This is the source of truth - always current, never stale
  // ─────────────────────────────────────────────────────────────────────────────
  const historyRef = useRef([]);
  const userIdRef = useRef(userId);
  const saveTimeoutRef = useRef(null);

  // Keep userIdRef in sync
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // CORE: Get history (synchronous, never stale)
  // ─────────────────────────────────────────────────────────────────────────────
  const getHistory = useCallback(() => {
    return historyRef.current;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CORE: Add a message pair (user + assistant)
  // ─────────────────────────────────────────────────────────────────────────────
  const addMessages = useCallback((userMessage, assistantMessage) => {
    const newMessages = [
      { role: 'user', content: String(userMessage) },
      { role: 'assistant', content: String(assistantMessage) }
    ];

    // Update ref immediately (synchronous)
    const updatedHistory = [...historyRef.current, ...newMessages].slice(-MAX_HISTORY_LENGTH);
    historyRef.current = updatedHistory;

    // Update state (triggers re-render)
    setHistory(updatedHistory);

    // Schedule save
    scheduleSave(updatedHistory);

    console.log(`💬 Conversation updated: ${updatedHistory.length} messages`);
    return updatedHistory;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CORE: Add single user message (before getting response)
  // Returns the history INCLUDING the new user message
  // ─────────────────────────────────────────────────────────────────────────────
  const addUserMessage = useCallback((userMessage) => {
    const newMessage = { role: 'user', content: String(userMessage) };

    // Update ref immediately
    const updatedHistory = [...historyRef.current, newMessage].slice(-MAX_HISTORY_LENGTH);
    historyRef.current = updatedHistory;

    // Update state
    setHistory(updatedHistory);

    console.log(`👤 User message added: ${updatedHistory.length} messages`);
    return updatedHistory;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CORE: Add assistant response (after getting response)
  // ─────────────────────────────────────────────────────────────────────────────
  const addAssistantMessage = useCallback((assistantMessage) => {
    const newMessage = { role: 'assistant', content: String(assistantMessage) };

    // Update ref immediately
    const updatedHistory = [...historyRef.current, newMessage].slice(-MAX_HISTORY_LENGTH);
    historyRef.current = updatedHistory;

    // Update state
    setHistory(updatedHistory);

    // Schedule save after assistant responds
    scheduleSave(updatedHistory);

    console.log(`🤖 Assistant message added: ${updatedHistory.length} messages`);
    return updatedHistory;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CORE: Set history directly (for loading from storage)
  // ─────────────────────────────────────────────────────────────────────────────
  const setHistoryDirect = useCallback((newHistory) => {
    const validHistory = Array.isArray(newHistory) ? newHistory : [];
    historyRef.current = validHistory.slice(-MAX_HISTORY_LENGTH);
    setHistory(historyRef.current);
    console.log(`📥 History set: ${historyRef.current.length} messages`);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CORE: Clear history (new conversation)
  // ─────────────────────────────────────────────────────────────────────────────
  const clearHistory = useCallback(async () => {
    historyRef.current = [];
    setHistory([]);

    // Clear from storage
    try {
      if (userIdRef.current) {
        await supabase
          .from('conversation_history')
          .delete()
          .eq('user_id', userIdRef.current);
        console.log('🗑️ Supabase history cleared');
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
        console.log('🗑️ Session history cleared');
      }
    } catch (error) {
      console.error('❌ Failed to clear history:', error);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // SAVE: Debounced save to storage
  // ─────────────────────────────────────────────────────────────────────────────
  const scheduleSave = useCallback((historyToSave) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        if (userIdRef.current) {
          // Save to Supabase for logged-in users
          await saveToSupabase(userIdRef.current, historyToSave);
        } else {
          // Save to sessionStorage for guests
          saveToSession(historyToSave);
        }
      } catch (error) {
        console.error('❌ Save failed:', error);
      } finally {
        setIsSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD: Load from storage on mount / userId change
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadHistory = async () => {
      try {
        let loaded = null;

        if (userId) {
          // Load from Supabase
          loaded = await loadFromSupabase(userId);
        } else {
          // Load from sessionStorage
          loaded = loadFromSession();
        }

        if (loaded && loaded.length > 0) {
          historyRef.current = loaded;
          setHistory(loaded);
          console.log(`✅ History loaded: ${loaded.length} messages`);
        }
      } catch (error) {
        console.error('❌ Failed to load history:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadHistory();

    // Cleanup save timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [userId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // MIGRATE: Move guest history to user account on login
  // ─────────────────────────────────────────────────────────────────────────────
  const migrateToUser = useCallback(async (newUserId) => {
    const guestHistory = loadFromSession();
    if (guestHistory && guestHistory.length > 0) {
      console.log('🔄 Migrating guest history to user account...');
      await saveToSupabase(newUserId, guestHistory);
      sessionStorage.removeItem(STORAGE_KEY);
      console.log('✅ Migration complete');
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // RETURN: Public API
  // ─────────────────────────────────────────────────────────────────────────────
  return {
    // State (for UI)
    history,
    isLoaded,
    isSaving,

    // Core functions (always use these)
    getHistory,        // () => array - SYNCHRONOUS, never stale
    addUserMessage,    // (msg) => array - Add user message, returns updated history
    addAssistantMessage, // (msg) => array - Add assistant message, saves to storage
    addMessages,       // (user, assistant) => array - Add both at once

    // Utility functions
    setHistoryDirect,  // (array) => void - Set history directly (for loading)
    clearHistory,      // () => void - Clear all history
    migrateToUser,     // (userId) => void - Migrate guest to user
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRIVATE: Storage Functions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Save to Supabase (logged-in users)
 */
async function saveToSupabase(userId, history) {
  const payload = {
    user_id: userId,
    history: history,
    updated_at: new Date().toISOString()
  };

  const { error } = await supabase
    .from('conversation_history')
    .upsert(payload, { 
      onConflict: 'user_id',
      ignoreDuplicates: false 
    });

  if (error) {
    // Fallback: try update then insert
    const { data: existing } = await supabase
      .from('conversation_history')
      .select('id')
      .eq('user_id', userId)
      .single();

    if (existing) {
      const { error: updateError } = await supabase
        .from('conversation_history')
        .update({ history, updated_at: payload.updated_at })
        .eq('user_id', userId);
      if (updateError) throw updateError;
    } else {
      const { error: insertError } = await supabase
        .from('conversation_history')
        .insert(payload);
      if (insertError) throw insertError;
    }
  }

  console.log('💾 Saved to Supabase');
}

/**
 * Load from Supabase (logged-in users)
 */
async function loadFromSupabase(userId) {
  const { data, error } = await supabase
    .from('conversation_history')
    .select('history')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found - new user
      return null;
    }
    throw error;
  }

  return data?.history || null;
}

/**
 * Save to sessionStorage (guests)
 */
function saveToSession(history) {
  try {
    const data = {
      history,
      savedAt: new Date().toISOString()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    console.log('💾 Saved to sessionStorage');
  } catch (error) {
    console.error('❌ sessionStorage save failed:', error);
  }
}

/**
 * Load from sessionStorage (guests)
 */
function loadFromSession() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);
    
    // Check expiry (24 hours)
    const savedTime = new Date(data.savedAt).getTime();
    const hoursDiff = (Date.now() - savedTime) / (1000 * 60 * 60);
    if (hoursDiff > 24) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data.history || null;
  } catch (error) {
    console.error('❌ sessionStorage load failed:', error);
    return null;
  }
}

export default useConversation;
